// src/contexts/BookingContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const BookingContext = createContext();

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within BookingProvider');
  }
  return context;
};

export const BookingProvider = ({ children }) => {
  const [bookings, setBookings] = useState([]);
  const [bookingDraft, setBookingDraft] = useState(null);

  // Load bookings from localStorage on mount
  useEffect(() => {
    const savedBookings = localStorage.getItem('serviceBookings');
    if (savedBookings) {
      try {
        setBookings(JSON.parse(savedBookings));
      } catch (error) {
        console.error('Error loading bookings:', error);
      }
    }
  }, []);

  // Save bookings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('serviceBookings', JSON.stringify(bookings));
  }, [bookings]);

  const addBooking = (service, selectedOptions = [], customerDetails = {}) => {
    // ✅ Calculate total with complementary options logic
    const totalPrice = calculateBookingTotal(service, selectedOptions);
    
    const booking = {
      id: `booking-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      service: {
        id: service.id,
        name: service.name,
        description: service.description,
        price: service.price,
        duration: service.duration,
        deliveryMethod: service.deliveryMethod,
        image: service.images?.[0] || null,
        optionGroups: service.optionGroups || [] // ✅ Include option groups for reference
      },
      selectedOptions: selectedOptions.filter(opt => opt.quantity > 0),
      customerDetails,
      totalPrice,
      status: 'pending',
      createdAt: new Date().toISOString()
    };

    setBookings(prev => [...prev, booking]);
    return booking;
  };

  // ✅ UPDATED: Calculate with complementary options
  const calculateBookingTotal = (service, selectedOptions = []) => {
    let total = parseFloat(service.price) || 0;
    
    // Group options by their option group for complementary calculation
    const optionsByGroup = {};
    
    selectedOptions.forEach((selectedOption, index) => {
      if (selectedOption.quantity <= 0) return;
      
      // Find which group this option belongs to
      const groupIndex = selectedOption.groupIndex ?? null;
      
      if (groupIndex !== null && service.optionGroups?.[groupIndex]) {
        if (!optionsByGroup[groupIndex]) {
          optionsByGroup[groupIndex] = [];
        }
        optionsByGroup[groupIndex].push({
          ...selectedOption,
          originalIndex: index
        });
      } else {
        // Standalone option (not in a group) or legacy format
        if (!selectedOption.isFree) {
          total += selectedOption.price * selectedOption.quantity;
        }
      }
    });
    
    // Calculate price for each option group with complementary logic
    Object.entries(optionsByGroup).forEach(([groupIndex, groupOptions]) => {
      const group = service.optionGroups[parseInt(groupIndex)];
      
      if (group.allFree) {
        // All options in this group are free - skip pricing
        return;
      }
      
      if (group.freeQuantity > 0) {
        // Calculate how many are free vs paid
        let totalQuantitySelected = 0;
        let sortedOptions = [];
        
        // Flatten all selected options with their quantities
        groupOptions.forEach(opt => {
          for (let i = 0; i < opt.quantity; i++) {
            sortedOptions.push({
              price: opt.price,
              name: opt.name
            });
          }
        });
        
        // Sort by price (ascending) so cheaper items are free first
        sortedOptions.sort((a, b) => a.price - b.price);
        
        // Apply complementary logic
        sortedOptions.forEach((opt, idx) => {
          if (idx >= group.freeQuantity) {
            // Beyond free quantity - charge for it
            total += opt.price;
          }
          // else: within free quantity - no charge
        });
        
      } else {
        // No complementary options - charge for all
        groupOptions.forEach(opt => {
          if (!opt.isFree) {
            total += opt.price * opt.quantity;
          }
        });
      }
    });
    
    return parseFloat(total.toFixed(2));
  };

  const updateBooking = (bookingId, updates) => {
    setBookings(prev =>
      prev.map(booking =>
        booking.id === bookingId
          ? { ...booking, ...updates }
          : booking
      )
    );
  };

  const removeBooking = (bookingId) => {
    setBookings(prev => prev.filter(booking => booking.id !== bookingId));
  };

  const clearBookings = () => {
    setBookings([]);
    setBookingDraft(null);
  };

  const getBookingsTotal = () => {
    return bookings.reduce((total, booking) => total + booking.totalPrice, 0);
  };

  const getBookingsCount = () => {
    return bookings.length;
  };

  const setDraftBooking = (draft) => {
    setBookingDraft(draft);
  };

  const clearDraftBooking = () => {
    setBookingDraft(null);
  };

  const value = {
    bookings,
    bookingDraft,
    addBooking,
    updateBooking,
    removeBooking,
    clearBookings,
    getBookingsTotal,
    getBookingsCount,
    setDraftBooking,
    clearDraftBooking,
    calculateBookingTotal
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};